import { initials } from "@dicebear/collection";
import { createAvatar } from "@dicebear/core";
import { ComponentProps } from "react";

import { useT } from "@/i18n";
import { AvatarFields } from "@/types";
import { getIconSize, IconSize } from "@/ui";
import { cn } from "@/utils/cn";
import { getUserFullName } from "@/utils/user";
import { resolveAvatarUrl } from "@/utils/user/resolveAvatarUrl";

export type UserAvatarProps = {
  user: { name: string | null; surname: string | null } & AvatarFields;
  width?: number;
  height?: number;
  size?: IconSize | number;
} & Omit<ComponentProps<"img">, "src" | "alt" | "aria-label">;

// TODO update it to use Avatar component from @touranted-com/ui
export const UserAvatar = ({ user, size, className, width, height, ...props }: UserAvatarProps) => {
  const t = useT();
  const fullName = getUserFullName(user);
  const sizePx = getIconSize(size);

  const generatedAvatar = createAvatar(initials, {
    seed: fullName ?? undefined,
    backgroundType: ["gradientLinear"],
    scale: 85,
  });

  const avatar = resolveAvatarUrl(user) ?? generatedAvatar.toDataUri();
  const label = t("user.components.avatar.label", { name: fullName });

  return (
    <img
      src={avatar}
      className={cn("shrink-0 rounded-full ", className)}
      style={{
        width: width ?? sizePx,
        height: height ?? sizePx,
        maxWidth: sizePx,
        maxHeight: sizePx,
        minWidth: sizePx,
        minHeight: sizePx,
      }}
      alt={label}
      aria-label={label}
      {...props}
    />
  );
};
